import { useEffect, useState } from "react";
import { ref, onValue, off } from "firebase/database";
import { doc, onSnapshot } from "firebase/firestore";
import { rtdb, fs } from "@/lib/firebase";
import type { ErrorEntry, PendingSubmit, QrCodeDoc, SessionStatus } from "@/lib/types";
import type { ParsedQr } from "@/lib/qrParse";

export type SessionPhase = "loading" | "open" | "submitting" | "invalid" | "closed";

export interface SessionState {
  phase: SessionPhase;
  qrCode: QrCodeDoc | null;
  pendingSubmit: PendingSubmit | null;
  rtdbStatus: SessionStatus | null;
  errors: Array<ErrorEntry & { id: string }>;
}

export const useSession = (parsed: ParsedQr | null, online: boolean = true): SessionState => {
  const [qrCode, setQrCode] = useState<QrCodeDoc | null>(null);
  const [qrLoaded, setQrLoaded] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<PendingSubmit | null>(null);
  const [rtdbStatus, setRtdbStatus] = useState<SessionStatus | null>(null);
  const [errors, setErrors] = useState<Array<ErrorEntry & { id: string }>>([]);

  useEffect(() => {
    if (!parsed) return;
    const qrRef = doc(fs, `Restaurant/${parsed.restoId}/QRCode/${parsed.qrId}`);
    const unsub = onSnapshot(qrRef, (snap) => {
      setQrCode(snap.exists() ? (snap.data() as QrCodeDoc) : null);
      setQrLoaded(true);
    });
    return unsub;
  }, [parsed?.restoId, parsed?.qrId]);

  useEffect(() => {
    if (!parsed) return;
    const sessionRef = ref(rtdb, `${parsed.restoId}/${parsed.qrId}`);
    const psRef = ref(rtdb, `${parsed.restoId}/${parsed.qrId}/pendingSubmit`);
    const stRef = ref(rtdb, `${parsed.restoId}/${parsed.qrId}/status`);
    const errRef = ref(rtdb, `${parsed.restoId}/${parsed.qrId}/errors`);

    const onPs = onValue(psRef, (snap) => {
      setPendingSubmit(snap.exists() ? (snap.val() as PendingSubmit) : null);
    });
    const onSt = onValue(stRef, (snap) => {
      const v = snap.val() as SessionStatus | null;
      setRtdbStatus(v ?? null);
    });
    const onErr = onValue(errRef, (snap) => {
      const val = (snap.val() as Record<string, ErrorEntry>) || {};
      const list = Object.entries(val).map(([id, e]) => ({ id, ...e }));
      list.sort((a, b) => (a.at || 0) - (b.at || 0));
      setErrors(list);
    });

    return () => {
      off(psRef, "value", onPs);
      off(stRef, "value", onSt);
      off(errRef, "value", onErr);
      // sessionRef left to garbage collection by Firebase
      void sessionRef;
    };
  }, [parsed?.restoId, parsed?.qrId]);

  let phase: SessionPhase = "loading";
  if (!parsed) phase = "invalid";
  else if (!qrLoaded) phase = "loading";
  // When offline, Firestore emits an empty snapshot from its in-memory cache.
  // Treat that as still-loading rather than an invalid QR — otherwise a flaky
  // network looks identical to a deactivated code to the customer.
  else if (!qrCode && !online) phase = "loading";
  // Même règle que la tablette (RestaurantRealtime.js:119-135) : tout statut
  // non nul est une session utilisable, sauf 3 = commande terminée (écran
  // dédié). 0/absent = « code introuvable ». Les functions appliquent la même
  // règle (functions/src/lib/session.ts).
  else if (!qrCode || !qrCode.status) phase = "invalid";
  else if (qrCode.status === 3) phase = "closed";
  else if (qrCode.qrToken && qrCode.qrToken !== parsed.qrToken) phase = "invalid";
  else if (pendingSubmit) phase = "submitting";
  else phase = "open";

  return { phase, qrCode, pendingSubmit, rtdbStatus, errors };
};
